import {
  Box,
  FormControl,
  FormErrorMessage,
  FormLabel,
  HStack,
  Input,
  Select,
  Switch,
  Text,
  VStack,
} from '@chakra-ui/react';
import { disabledInputStyles } from '@diamond/shared/ui';
import { useEffect } from 'react';
import { UseFormReturn } from 'react-hook-form';

import type { CreateVoucherRequest } from '../../types';

interface VoucherUsageFormProps {
  form: UseFormReturn<CreateVoucherRequest>;
  disabled?: boolean;
}

export function VoucherUsageForm({
  form,
  disabled = false,
}: VoucherUsageFormProps) {
  const {
    register,
    setValue,
    watch,
    formState: { errors },
  } = form;

  // Watch the nested configuration fields
  const conditionConfiguration = watch('condition_configuration');

  // For UI purposes, derive flat values from nested structure
  const combineVoucher =
    conditionConfiguration?.is_allow_combine_other_voucher ?? false;
  const combinePromo =
    conditionConfiguration?.is_allow_combine_other_promo ?? false;
  const allowSpecialPrice =
    conditionConfiguration?.is_allow_combine_other_special_price ?? false;

  // Watch condition type for conditional display
  const conditionType = conditionConfiguration?.condition_type ?? 'REGULAR';
  const showMinPurchaseInput = conditionType === 'MIN_PURCHASE';

  useEffect(() => {
    // Initialize nested configuration if it doesn't exist
    if (!conditionConfiguration) {
      setValue('condition_configuration', {
        condition_type: 'REGULAR',
        is_allow_combine_other_voucher: false,
        is_allow_combine_other_promo: false,
        is_allow_combine_other_special_price: false,
        min_purchase_amount: null,
      });
    }
  }, [conditionConfiguration, setValue]);

  useEffect(() => {
    // Clear min_purchase_amount when switching away from MIN_PURCHASE
    if (
      conditionType !== 'MIN_PURCHASE' &&
      conditionConfiguration?.min_purchase_amount !== null
    ) {
      setValue('condition_configuration.min_purchase_amount', null, {
        shouldValidate: true,
      });
    }
  }, [conditionType, setValue, conditionConfiguration?.min_purchase_amount]);

  return (
    <VStack spacing={6} align="stretch">
      <Text fontSize="lg" fontWeight="semibold" color="gray.800">
        Syarat Penggunaan Voucher
      </Text>

      <Box display="flex" gap={6}>
        <FormControl
          isInvalid={!!errors.condition_configuration?.condition_type}
        >
          <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
            Jenis syarat
          </FormLabel>
          <Select
            placeholder="Pilih jenis syarat"
            fontSize="sm"
            {...register('condition_configuration.condition_type')}
            isDisabled={disabled}
            sx={disabledInputStyles}
          >
            <option value="REGULAR">Reguler</option>
            <option value="MIN_PURCHASE">Minimal belanja</option>
          </Select>
          <FormErrorMessage>
            {errors.condition_configuration?.condition_type?.message as string}
          </FormErrorMessage>
        </FormControl>

        {/* Conditional Min Purchase Amount Input */}
        {showMinPurchaseInput && (
          <FormControl
            isInvalid={!!errors.condition_configuration?.min_purchase_amount}
          >
            <FormLabel fontSize="sm" fontWeight="medium" color="gray.700">
              Minimal nominal transaksi (Rp)
            </FormLabel>
            <Input
              type="number"
              min={0}
              step={1000}
              fontSize="sm"
              placeholder="Masukkan nominal minimal belanja"
              {...register('condition_configuration.min_purchase_amount', {
                setValueAs: (value) => {
                  // Handle empty string or non-numeric input
                  if (value === '' || value === null || value === undefined) {
                    return null;
                  }
                  const parsed = parseFloat(value);
                  return isNaN(parsed) ? null : parsed;
                },
              })}
              isDisabled={disabled}
              sx={disabledInputStyles}
            />
            <FormErrorMessage>
              {
                errors.condition_configuration?.min_purchase_amount
                  ?.message as string
              }
            </FormErrorMessage>
          </FormControl>
        )}
      </Box>

      <VStack spacing={4} align="stretch">
        <UsageToggle
          isDisabled={disabled}
          label="Kombinasi dengan voucher lain"
          isChecked={combineVoucher}
          onChange={(value) =>
            setValue(
              'condition_configuration.is_allow_combine_other_voucher',
              value,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            )
          }
        />
        <UsageToggle
          isDisabled={disabled}
          label="Kombinasi dengan promo lain"
          isChecked={combinePromo}
          onChange={(value) =>
            setValue(
              'condition_configuration.is_allow_combine_other_promo',
              value,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            )
          }
        />
        <UsageToggle
          isDisabled={disabled}
          label="Pemakaian dengan Price List Khusus (PLK)"
          isChecked={allowSpecialPrice}
          onChange={(value) =>
            setValue(
              'condition_configuration.is_allow_combine_other_special_price',
              value,
              {
                shouldDirty: true,
                shouldValidate: true,
              }
            )
          }
        />
      </VStack>
    </VStack>
  );
}

interface UsageToggleProps {
  label: string;
  isChecked: boolean;
  isDisabled?: boolean;
  onChange: (value: boolean) => void;
}

function UsageToggle({
  label,
  isChecked,
  isDisabled,
  onChange,
}: UsageToggleProps) {
  return (
    <HStack justify="space-between" align="center">
      <Text fontSize="sm" color="gray.700">
        {label}
      </Text>
      <HStack spacing={2} align="center">
        <Text fontSize="xs" color={isChecked ? 'gray.400' : 'gray.800'}>
          Tidak
        </Text>
        <Switch
          disabled
          // isDisabled={isDisabled}
          isChecked={isChecked}
          onChange={(event) => onChange(event.target.checked)}
          colorScheme="linkedin"
        />
        <Text fontSize="xs" color={isChecked ? 'gray.800' : 'gray.400'}>
          Bisa
        </Text>
      </HStack>
    </HStack>
  );
}
